import { useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toastApiError } from "@/lib/toastApiError";

interface ImageUploadFieldProps {
  /** Existing image URL from the server. */
  remoteUrl?: string;
  /** Locally selected file, sent as multipart `image` on save. */
  pendingFile?: File | null;
  onPendingFileChange: (file: File | null) => void;
  /** True when the existing image is marked to be removed on save. */
  markedForRemoval?: boolean;
  /** Request removal of the existing server image (applied on save). */
  onRemove?: () => void;
  /** Undo a pending removal. */
  onUndoRemove?: () => void;
  /** Busy state when the parent submits the term. */
  busy?: boolean;
}

export function ImageUploadField({
  remoteUrl,
  pendingFile,
  onPendingFileChange,
  markedForRemoval = false,
  onRemove,
  onUndoRemove,
  busy = false,
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const localPreviewUrl = useMemo(
    () => (pendingFile ? URL.createObjectURL(pendingFile) : null),
    [pendingFile]
  );

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const showPending = pendingFile != null;
  const showRemote =
    !showPending && !markedForRemoval && !!remoteUrl?.trim();
  const previewSrc = showPending
    ? localPreviewUrl || ""
    : showRemote
      ? remoteUrl!.trim()
      : "";

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (
      file.type.startsWith("image/") ||
      /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(file.name)
    ) {
      onPendingFileChange(file);
    } else {
      toastApiError("Please choose an image file (PNG, JPG, WEBP, …).");
    }
    e.target.value = "";
  };

  return (
    <div className="grid gap-2">
      <Label>Image</Label>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.png,.jpg,.jpeg,.gif,.webp,.svg,.avif"
        className="hidden"
        onChange={handlePick}
      />
      {previewSrc ? (
        <div className="bg-muted/30 flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center">
          <img
            src={previewSrc}
            alt="Term"
            className="h-20 w-20 shrink-0 rounded-md border object-cover"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
            >
              {pendingFile ? "Choose a different image" : "Replace image"}
            </Button>
            {pendingFile ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => !busy && onPendingFileChange(null)}
                disabled={busy}
              >
                Remove new image
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => !busy && onRemove?.()}
                disabled={busy}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                Remove image
              </Button>
            )}
          </div>
        </div>
      ) : markedForRemoval ? (
        <div className="bg-muted/30 flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed p-3">
          <span className="text-muted-foreground text-sm">
            Image will be removed when you save.
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
            >
              Attach image
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => !busy && onUndoRemove?.()}
              disabled={busy}
            >
              Undo
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="justify-start"
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
            </>
          ) : (
            <>
              <ImagePlus className="mr-2 h-4 w-4" /> Attach image
            </>
          )}
        </Button>
      )}
    </div>
  );
}
